import { Ban, DollarSign, Edit } from 'lucide-react'; // Icons for edit and block actions
import React, { useEffect, useMemo, useState } from 'react';
import { Container, FormControl, InputGroup } from 'react-bootstrap';
import styled from 'styled-components';
import GenericButton from '../../components/GenericButton';
import GenericDropdown from '../../components/GenericDropDown';
import GenericModal from '../../components/GenericModal';
import GenericTable from '../../components/GenericTable';
import { useAuth } from '../../context/AuthContext';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { User } from '../../models/Users';
import { addDynamicLimit, getAllGames } from '../../redux/entities/gameEntity';
import { addPoints, getAllUsers, revokePoints, saveUser, toggleUserStatus, updateUser } from '../../redux/entities/userEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { Role } from '../../utils/rolesUtil';
import AddUserPage from '../AddUserPage';
import AddPointsPage from '../Users/AddPointsPage';

const PageHeading = styled.h2`
  margin-top: 2rem;
  font-weight: 600;
  color: #4a90e2;
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
`;

const SearchBox = styled(InputGroup)`
  max-width: 300px;
  flex-grow: 1; /* Allow the search box to take up available space */
  margin-right: 15px;
  margin-bottom: 1rem;

  @media (max-width: 576px) {
    margin-right: 0;
    max-width: 100%; /* Ensure full width on small screens */
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 2rem;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionIcon = styled.div`
  cursor: pointer;
  color: #555;
  font-size: 1.5rem;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }
`;

const DynamicLimitPage: React.FC = () => {
  const [modalShow, setModalShow] = useState(false);
  const [modalPointShow, setModalPointShow] = useState({
    action: '', // add / remove
    show: false  // true | false
  });
  const [selectedItem, setSelectedItem] = useState<User>({} as User);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAdmin, setSelectedAdmin] = useState<any>();
  const [selectedGame, setSelectedGame] = useState<any>();
  const [multiplier, setMultiplier] = useState<any>();
  const [selectedAgent, setSelectedAgent] = useState<any>();
  const [selectedCustomer, setSelectedCustomer] = useState<any>();
  const { role } = useAuth();


  const dispatch = useAppDispatch();
  const { showModalStrip } = useModalStrip();
  const rawUsers = useAppSelector((state) => state.entities.userReducer.users);
  const users = useMemo(() => (Array.isArray(rawUsers) ? rawUsers : []), [rawUsers]);

  let games = useAppSelector((state) => state.entities.gameReducer.games);

  useEffect(() => {
    // Dispatch getAllGames action only if games are not already loaded
    if (games && games.length === 0) {
      dispatch(getAllGames())
        .unwrap()
        .catch((err: Error) => {
          showModalStrip('danger', err.message, 5000);
        });
    }
  }, [games, dispatch, showModalStrip]);

  useEffect(() => {
    dispatch(getAllUsers(0)); // Fetch users if not already available in Redux
  }, [dispatch]);


  const handleDelete = (user: User) => {
    dispatch(toggleUserStatus(user.id)).unwrap().then(res => {
      getAllUsers(0);
    }).catch(err => {
      showModalStrip('danger', "Failed to fetch the users", 5000);
    });
  };

  const handleOpenModal = (item: User, mode: string, role: string) => {
    setSelectedItem({
      ...item,
      role: role
    });
    if (mode === 'add') {
      setModalShow(true);
    } else {
      if (item.status === 'INACTIVE') {
        return;
      }
      setModalShow(true);
    }
  };

  const handleOpenPointsModal = (item: User, action: string) => {
    setSelectedItem(item);
    setModalPointShow({
      ...modalPointShow,
      action: action,
      show: true
    });
  };

  const handleSaveUser = (user: User) => {
    if (user.id) {
      dispatch(updateUser(user)).unwrap().then((res) => {
        setModalShow(false);
        dispatch(getAllUsers(0))
      }).catch((err: any) => {
        showModalStrip('danger', err?.message || err, 5000);
      })
    } else {
      dispatch(saveUser(user)).unwrap().then((res) => {
        setModalShow(false);
        dispatch(getAllUsers(0))
      }).catch((err: any) => {
        showModalStrip('danger', err?.message || err, 5000);
      })
    }
  }

  const handleAddDynamicLimit = () => {
    const dynamicLimit = {
      customer: selectedCustomer,
      game: selectedGame,
      multiplier: parseInt(multiplier)
    }

    console.log(dynamicLimit);
    dispatch(addDynamicLimit(dynamicLimit));
  }

  const handleAddPoints = (user: User, point: any, action: string) => {
    if (action === 'add') {
      dispatch(addPoints({ user, point })).unwrap().then((res) => {
        setModalPointShow({
          ...modalPointShow,
          action: '',
          show: false
        });
        dispatch(getAllUsers(0))
      }).catch((err: Error) => {
        showModalStrip('danger', err.message, 5000);
      })
    } else if (action === 'remove') {
      dispatch(revokePoints({ user, point })).unwrap().then((res) => {
        setModalPointShow({
          ...modalPointShow,
          action: '',
          show: false
        });
        dispatch(getAllUsers(0))
      }).catch((err: Error) => {
        showModalStrip('danger', err.message, 5000);
      })
    } else {
      return;
    }
  }



  const isInactive = (item: any) => {
    return item?.status === 'INACTIVE';  // Assuming 'status' is a field in the data
  }

  // Directly filter users based on role
  const getFilteredUsers = (parent: string, parentUser: User | null) => {
    if (parent === Role.Admin) {
      return users.filter((user) => user && user.role === Role.Admin);
    }
    if (parent === Role.Agent) {
      return parentUser ? (parentUser.children ? parentUser.children : []) : users.filter((user) => user && user.role === Role.Agent);
    }
    if (parent === Role.Customer) {
      const customers = parentUser ? (parentUser.children ? parentUser.children : []) : users.filter((user) => user && user.role === Role.Customer);
      return customers;
    }
    return [];
  };

  return (
    <Container>
      <PageHeading>Dynamic Limit</PageHeading>

      <TableWrapper>
        {/* SuperAdmin selects Admin */}
        {role === Role.SuperAdmin && (
          <GenericDropdown
            label="Select Admin"
            options={getFilteredUsers(Role.Admin, null)?.map((admin) => ({
              value: admin.id.toString(),
              label: admin.name,
            }))}
            onChange={(value) => setSelectedAdmin(users?.find((user) => user?.id?.toString() === value))}
            value={selectedAdmin?.id?.toString()}
          />
        )}

        {/* Admin selects Agent */}
        {(role === Role.SuperAdmin || role === Role.Admin) && (
          <GenericDropdown
            label="Select Agent"
            options={getFilteredUsers(Role.Agent, selectedAdmin)?.map((agent) => ({
              value: agent.id.toString(),
              label: agent.name,
            }))}
            onChange={(value) => setSelectedAgent(selectedAdmin
              ? selectedAdmin.children?.find((user: any) => user?.id?.toString() === value)
              : users.find((user) => user?.id?.toString() === value))}
            value={selectedAgent?.id?.toString()}
          />
        )}

        {/* Agent selects Customer */}
        {(role === Role.SuperAdmin || role === Role.Admin || role === Role.Agent) && (
          <GenericDropdown
            label="Select Customer"
            options={getFilteredUsers(Role.Customer, selectedAgent)?.map((agent) => ({
              value: agent.id.toString(),
              label: agent.name,
            }))}
            onChange={(value) => setSelectedCustomer(selectedAgent
              ? selectedAgent.children?.find((user: any) => user?.id?.toString() === value)
              : users.find((user) => user?.id?.toString() === value))}
            value={selectedCustomer?.id?.toString()}
          />
        )}

        <GenericDropdown
          label="Select Game"
          options={games.filter(x => x.status === 'ACTIVE').map((game) => ({
            value: game.id.toString(),
            label: game.name,
          }))}
          onChange={(value) => setSelectedGame(games.find((user) => user?.id?.toString() === value))}
          value={selectedGame?.id?.toString()}
        />

        <GenericDropdown
          label="Select Dynamic Limit"
          options={["1", "2", "3", "4", "5"].map((game) => ({
            value: game,
            label: game,
          }))}
          onChange={(id) => setMultiplier(id)}
          value={multiplier}
        />

        <GenericButton
          label='Add Limit'
          onClick={() => handleAddDynamicLimit()}
        />

        {/* Search Box */}
        <SearchBox>
          <FormControl
            placeholder="Search by name or email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBox>

        <GenericTable
          columns={[
            { header: 'ID', field: 'id' },
            { header: 'Name', field: 'name' },
            { header: 'Phone', field: 'mobile' },
            { header: 'Points', field: 'points',render(item) {
              return item.points? Number(item.points).toFixed(2) : 0;
            } },
            { header: 'Digit View', field: 'canSeeDigitView' },
            { header: 'Actions', field: 'actions' },
          ]}
          data={getFilteredUsers(Role.Customer, selectedAgent).map((item) => ({
            ...item,
            actions: (
              <ActionButtonContainer>
                <ActionIcon onClick={() => handleOpenModal(item, 'edit', Role.Customer)}>
                  <Edit />
                </ActionIcon>
                <ActionIcon onClick={() => handleOpenPointsModal(item, 'add')}>
                  <DollarSign color={isInactive(item) ? '#bb2124' : '#0d4a14'} />
                </ActionIcon>
                <ActionIcon onClick={() => handleOpenPointsModal(item, 'remove')}>
                  <DollarSign color={'#bb2124'} />
                </ActionIcon>
                <ActionIcon onClick={() => handleDelete(item)}>
                  <Ban color={isInactive(item) ? '#bb2124' : '#0d4a14'} />
                </ActionIcon>
              </ActionButtonContainer>
            ),
          }))}
          actions={false} // Don't display the generic edit and delete buttons, we are using custom actions
          pagination={true}
        />
      </TableWrapper>

      {modalShow && (
        <GenericModal
          isOpen={modalShow}
          onClose={() => setModalShow(false)}
          node={<AddUserPage data={selectedItem as User} onSave={(user) => handleSaveUser(user)} />}
          label={selectedItem && selectedItem.id ? `Update User - ${selectedItem.role}` : `Add New - ${selectedItem.role}`}
        />
      )}

      {modalPointShow && modalPointShow.show && (
        <GenericModal
          isOpen={modalPointShow.show}
          onClose={() => setModalPointShow({ ...modalPointShow, show: false })}
          node={<AddPointsPage data={selectedItem as User} action={modalPointShow.action} onSave={(point) => handleAddPoints(selectedItem, point, modalPointShow.action)} />}
          label={modalPointShow.action === 'add' ? `Add Points` : 'Remove Points'}
        />
      )}
    </Container>
  );
};

export default DynamicLimitPage;
